/**
 * @file Lot de pieces rendu en 2 draw calls (BatchedMesh) : un maillage de remplissage (toon a couleurs de sommets + couleur d'instance)
 *       et un maillage de contour inverted-hull (ToonKit), pour un nombre quelconque de geometries DIFFERENTES (variantes d'arbres,
 *       os de chats, armes...). Chaque piece = une geometrie enregistree une fois (cle) + des instances (matrice + couleur).
 */
import { BatchedMesh, Color } from 'three';
import type { BufferGeometry, Matrix4, Scene } from 'three';
import type { ToonKit } from '../toon/toon';

/** Instances d'une piece : une dans le lot de remplissage, une dans celui du contour (-1 : pas de contour). */
export interface PartHandle {
  fill: number;
  hull: number;
}

interface GeoIds {
  fill: number;
  hull: number;
}

/** Une piece a enregistrer : remplissage a couleurs de sommets (position, normal, color ; non indexe) + source du contour (position). */
export interface PartSource {
  fill: BufferGeometry;
  hull: BufferGeometry | null;
  /** Vrai : geometries temporaires, disposees apres copie dans le lot (sinon partagees : l'appelant les possede). */
  temp?: boolean;
}

const WHITE = new Color(1, 1, 1);

export class PartBatch {
  readonly fill: BatchedMesh;
  readonly hull: BatchedMesh;
  private readonly geos = new Map<string, GeoIds>();
  private fillCap: number;
  private hullVertCap: number;
  private hullIdxCap: number;
  private fillUsed = 0;
  private hullVertUsed = 0;
  private hullIdxUsed = 0;
  private disposed = false;

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    maxInstances: number,
    vertexCapacity = 48000,
  ) {
    this.fillCap = vertexCapacity;
    this.hullVertCap = Math.ceil(vertexCapacity / 3);
    this.hullIdxCap = this.hullVertCap * 8;
    this.fill = new BatchedMesh(maxInstances, this.fillCap, 1, kit.toon('#ffffff', { vertexColors: true }));
    this.hull = new BatchedMesh(maxInstances, this.hullVertCap, this.hullIdxCap, kit.outline());
    for (const m of [this.fill, this.hull]) {
      m.frustumCulled = false;
      m.perObjectFrustumCulled = false;
      m.sortObjects = false;
    }
    this.hull.renderOrder = -1;
    this.hull.name = 'outline';
    // rien a dessiner tant qu'aucune geometrie n'est enregistree
    this.fill.visible = false;
    this.hull.visible = false;
    scene.add(this.fill, this.hull);
  }

  /** Enregistre une piece sous `key` (une seule fois : `make` n'est appelee que si la cle est inconnue). */
  register(key: string, make: () => PartSource): void {
    if (this.geos.has(key)) return;
    const src = make();
    const verts = src.fill.getAttribute('position').count;
    this.growFill(verts);
    const fill = this.fill.addGeometry(src.fill);
    this.fillUsed += verts;
    let hull = -1;
    if (src.hull) {
      const h = this.kit.buildHull(src.hull);
      const hv = h.getAttribute('position').count;
      const hi = h.getIndex()?.count ?? 0;
      this.growHull(hv, hi);
      hull = this.hull.addGeometry(h);
      this.hullVertUsed += hv;
      this.hullIdxUsed += hi;
      h.dispose();
    }
    if (src.temp) {
      src.fill.dispose();
      src.hull?.dispose();
    }
    this.geos.set(key, { fill, hull });
    this.fill.visible = true;
    this.hull.visible = true;
  }

  has(key: string): boolean {
    return this.geos.has(key);
  }

  /** Cree les instances d'une piece enregistree ; null si le lot est plein ou la cle inconnue. */
  add(key: string): PartHandle | null {
    const g = this.geos.get(key);
    if (!g || this.disposed) return null;
    if (this.fill.instanceCount >= this.fill.maxInstanceCount) return null;
    const fill = this.fill.addInstance(g.fill);
    this.fill.setColorAt(fill, WHITE);
    let hull = -1;
    if (g.hull >= 0 && this.hull.instanceCount < this.hull.maxInstanceCount) hull = this.hull.addInstance(g.hull);
    return { fill, hull };
  }

  /** Matrice monde + couleur d'instance (multiplie la couleur des sommets ; > 1 = eclaircit). */
  set(h: PartHandle, matrix: Matrix4, color?: Color): void {
    this.fill.setMatrixAt(h.fill, matrix);
    this.fill.setColorAt(h.fill, color ?? WHITE);
    if (h.hull >= 0) this.hull.setMatrixAt(h.hull, matrix);
  }

  remove(h: PartHandle): void {
    this.fill.deleteInstance(h.fill);
    if (h.hull >= 0) this.hull.deleteInstance(h.hull);
  }

  /** Nombre de geometries distinctes enregistrees (diagnostic). */
  get geometryCount(): number {
    return this.geos.size;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.fill, this.hull);
    this.fill.dispose();
    this.hull.dispose();
    this.geos.clear();
  }

  // ------------------------------------------------------------------------------------------------------------------

  private growFill(extra: number): void {
    if (this.fillUsed + extra <= this.fillCap) return;
    let cap = this.fillCap;
    while (this.fillUsed + extra > cap) cap *= 2;
    this.fill.setGeometrySize(cap, 1);
    this.fillCap = cap;
  }

  private growHull(extraV: number, extraI: number): void {
    if (this.hullVertUsed + extraV <= this.hullVertCap && this.hullIdxUsed + extraI <= this.hullIdxCap) return;
    let vc = this.hullVertCap;
    let ic = this.hullIdxCap;
    while (this.hullVertUsed + extraV > vc) vc *= 2;
    while (this.hullIdxUsed + extraI > ic) ic *= 2;
    this.hull.setGeometrySize(vc, ic);
    this.hullVertCap = vc;
    this.hullIdxCap = ic;
  }
}
